import { HelpCircle, Info } from "lucide-react";
import type { ReactNode } from "react";

export interface GlossaryTerm {
  term: string;
  plainEnglish: string;
  definition: string;
  example?: string;
}

export const BEGINNER_GLOSSARY: Record<string, GlossaryTerm> = {
  "The Signal": {
    term: "The Signal",
    plainEnglish: "the platform's research radar",
    definition: "The Signal is the platform's watchlist-style research surface for stocks that may deserve a closer look.",
    example: "If a stock appears in The Signal, it means it passed the research filters for that lane; it is not an order instruction.",
  },
  "Signal label": {
    term: "Signal label",
    plainEnglish: "the platform's original result tag for the signal",
    definition: "A Signal label is the platform's original result tag for the signal, used for review and reporting.",
    example: "The beginner help may explain a label, but it does not rename or recalculate it.",
  },
  "Official Signal labels": {
    term: "Official Signal labels",
    plainEnglish: "the platform's original result tag for the signal",
    definition: "Official Signal labels are the platform's original result tags for signals. They stay unchanged here.",
  },
  "Lane Routing": {
    term: "Lane Routing",
    plainEnglish: "sorting a stock into the right research bucket",
    definition: "Lane Routing sorts a stock into the right research bucket before judging whether it belongs in The Signal.",
    example: "A larger company can be routed to the Large-Cap Signal Lane instead of being treated like a failed small-cap setup.",
  },
  "Candidate": {
    term: "Candidate",
    plainEnglish: "a stock being reviewed",
    definition: "A Candidate is a stock being reviewed by The Signal research flow.",
  },
  "Candidate Promotion": {
    term: "Candidate Promotion",
    plainEnglish: "moving a reviewed stock into the next research stage",
    definition: "Candidate Promotion is the existing platform process that advances a candidate when it meets the configured rules.",
  },
  "Catalyst": {
    term: "Catalyst",
    plainEnglish: "news or activity that can explain attention around a stock",
    definition: "A Catalyst is a real-world event or development that may help explain why a stock is moving or being watched.",
  },
  "Catalyst-to-Mover": {
    term: "Catalyst-to-Mover",
    plainEnglish: "checking whether news is followed by price and volume movement",
    definition: "Catalyst-to-Mover compares a catalyst with later price and volume behavior to see whether the event appears to be followed by market attention.",
  },
  "Outcome Intelligence": {
    term: "Outcome Intelligence",
    plainEnglish: "learning from what happened after earlier signals",
    definition: "Outcome Intelligence summarizes what happened after prior Signal candidates so operators can review patterns without changing today's labels.",
  },
  "Root Cause": {
    term: "Root Cause",
    plainEnglish: "the main reason something happened",
    definition: "Root Cause is the clearest explanation the platform can show for why a result, alert, or movement happened.",
  },
  "Follow-Through": {
    term: "Follow-Through",
    plainEnglish: "whether the move keeps developing after the first signal",
    definition: "Follow-Through means checking whether a stock continues to show confirming behavior after the first move or catalyst.",
  },
  "Institutional Catalyst": {
    term: "Institutional Catalyst",
    plainEnglish: "important news or activity from major companies, governments, or large institutions",
    definition: "An Institutional Catalyst is important news or activity from major companies, governments, or large institutions that may deserve research attention.",
    example: "Examples include a major contract, regulatory decision, large partnership, or government action.",
  },
  "Price Reaction": {
    term: "Price Reaction",
    plainEnglish: "how the stock price responded",
    definition: "Price Reaction describes how the stock price moved after a catalyst or signal appeared.",
  },
  "Volume Confirmation": {
    term: "Volume Confirmation",
    plainEnglish: "whether trading activity increased enough to support the move",
    definition: "Volume Confirmation checks whether trading activity increased enough to support the idea that market attention is real.",
  },
  "Provider Health": {
    term: "Provider Health",
    plainEnglish: "whether the data feeds powering the page are working",
    definition: "Provider Health is the platform's honest readout of whether each data source (quotes, news, filings, etc.) is fresh, lagging, or unavailable right now.",
    example: "If a provider shows Cached or Stale, the page is still showing the last good data, not made-up data.",
  },
  "Pending": {
    term: "Pending",
    plainEnglish: "the platform is still waiting for data or for a scheduled job to finish",
    definition: "Pending means the platform has not yet produced this result. The page does not fabricate values while waiting.",
    example: "Outcome labels show Pending until the daily labeling job runs after the market window closes.",
  },
  "Unavailable": {
    term: "Unavailable",
    plainEnglish: "the data source could not be reached right now",
    definition: "Unavailable means the provider did not return data in time. The platform marks the surface explicitly instead of guessing.",
    example: "If quotes show Unavailable, no spread or volume figures will be invented; the row simply waits.",
  },
  "Data Freshness": {
    term: "Data Freshness",
    plainEnglish: "how recent the data on screen actually is",
    definition: "Data Freshness shows how long it has been since the underlying provider last returned new data.",
    example: "Live = within the freshness window; Cached = older but still usable; Stale = outside the freshness window.",
  },
  "Spread": {
    term: "Spread",
    plainEnglish: "the gap between the bid and the ask",
    definition: "Spread is the difference between the highest buy price and the lowest sell price quoted right now. Wide spreads usually mean the market is thinner or less liquid.",
    example: "A 50 bps (half a percent) spread on a small-cap is a sign of low liquidity, not necessarily a trading opportunity.",
  },
  "Liquidity": {
    term: "Liquidity",
    plainEnglish: "how easy it is to trade a stock without moving the price",
    definition: "Liquidity describes the ability to buy or sell shares without large price swings, generally implied from spread and volume together.",
    example: "Thin volume + wide spread = low liquidity = harder to enter or exit cleanly.",
  },
  "Volume": {
    term: "Volume",
    plainEnglish: "how many shares have changed hands",
    definition: "Volume is the number of shares traded over a period. Premarket volume is shown for The Signal candidates because it hints at interest before the open.",
    example: "A premarket-volume column showing 500K means about half a million shares traded before the regular session opened.",
  },
  "Watch Status": {
    term: "Watch Status",
    plainEnglish: "the platform-defined research state of a stock right now",
    definition: "Watch Status is the platform's research state for a candidate — for example whether it is being followed, has dropped out of the lane, or is awaiting confirmation.",
    example: "Watch Status is informational only; it is not a buy or sell instruction.",
  },
  "Trade Classification": {
    term: "Trade Classification",
    plainEnglish: "an educational category for the kind of setup being reviewed",
    definition: "Trade Classification groups a Signal candidate into an educational research category so users can understand the setup type.",
  },
  "Strategy Posture": {
    term: "Strategy Posture",
    plainEnglish: "Adversyn's current stance toward a market area",
    definition: "A strategy posture is Adversyn's current stance toward a market area. It tells you whether we are avoiding it, watching it, preparing for a possible opportunity, or treating it as a higher-conviction candidate. It is not automatically a buy or sell instruction.",
  },
  "AI Stack Strategy": {
    term: "AI Stack Strategy",
    plainEnglish: "theme/sector posture across AI infrastructure",
    definition: "AI Stack Strategy is a theme- and sector-posture surface, not a recommendation system. It does not issue direct buy or sell instructions; those belong in the Decision Layer.",
  },
  "Conviction Tracker": {
    term: "Conviction Tracker",
    plainEnglish: "public-disclosure-driven conviction signals",
    definition: "The Conviction Tracker reads public SEC disclosures and tracks where institutional conviction is forming. It is a research signal, not a buy instruction.",
  },
  "AI Council": {
    term: "AI Council",
    plainEnglish: "multi-model deliberation surface",
    definition: "The AI Council assembles multi-model perspectives for research. It is advisory; recommendations are gated in the Decision Layer.",
  },
  "Market Pulse": {
    term: "Market Pulse",
    plainEnglish: "market overview and provider-health snapshot",
    definition: "Market Pulse summarizes regime context, movers, news catalysts, and the health of the data providers powering everything else. It is read-only research.",
  },
  "Watch": {
    term: "Watch",
    plainEnglish: "being monitored, not a buy instruction",
    definition: "Watch means the platform is monitoring the area or symbol because it passed research filters. It does not mean the platform is recommending a buy.",
  },
  "Decision Layer": {
    term: "Decision Layer",
    plainEnglish: "where Adversyn turns research into gated recommendations",
    definition: "Direct buy/sell/reduce/avoid recommendations live in the Adversyn Decision Layer. The Decision Layer applies user investment profile, account mode (demo/paper/live), risk tolerance, time horizon, capital allocation, liquidity, concentration limit, suitability, and compliance gates before any recommendation is presented. Research surfaces like AI Stack Strategy, The Signal, the Conviction Tracker, the AI Council, and Market Pulse never directly issue buy/sell instructions.",
  },
};

export function getTermHelp(term: string): GlossaryTerm {
  return BEGINNER_GLOSSARY[term] || {
    term,
    plainEnglish: term,
    definition: `${term} is explained in plain English for this section.`,
  };
}

export function BeginnerHelpCard({
  testId,
  title = "What this section means",
  what,
  why,
  lookAt,
  notDo,
  children,
}: {
  testId?: string;
  title?: string;
  what: ReactNode;
  why: ReactNode;
  lookAt: ReactNode;
  notDo: ReactNode;
  children?: ReactNode;
}) {
  const rows = [
    ["What is this?", what],
    ["Why it matters", why],
    ["What to look at", lookAt],
    ["What it does NOT do", notDo],
  ] as const;
  return (
    <aside
      data-testid={testId}
      className="my-3 border-l-2 border-emerald-500/50 bg-emerald-500/5 px-3 py-2 text-[12px] text-[var(--muted-text)]"
    >
      <div className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-emerald-400">
        <Info className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {rows.map(([label, body]) => (
          <div key={label} className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-[var(--app-text)]">{label}</div>
            <div className="mt-0.5 leading-relaxed">{body}</div>
          </div>
        ))}
      </div>
      {children ? <div className="mt-2 flex flex-wrap gap-1.5">{children}</div> : null}
    </aside>
  );
}

export function SectionExplainer(props: Parameters<typeof BeginnerHelpCard>[0]) {
  return <BeginnerHelpCard {...props} />;
}

export function HelpTooltip({ term, definition, example }: { term: string; definition: string; example?: string }) {
  return (
    <details className="inline-block align-baseline">
      <summary className="inline-flex cursor-help list-none items-center gap-1 rounded border border-[var(--card-border)] bg-[var(--input-bg)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--app-text)]">
        <HelpCircle className="h-3 w-3 text-emerald-400" /> {term}
      </summary>
      <span className="mt-1 block max-w-sm rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] p-2 text-[11px] leading-relaxed text-[var(--muted-text)] shadow-lg">
        {definition}
        {example ? <span className="mt-1 block text-[var(--app-text)]">Example: {example}</span> : null}
      </span>
    </details>
  );
}

export function ClickToExplain(props: { term: string; definition: string; example?: string }) {
  return <HelpTooltip {...props} />;
}

export function TermHelp({ term }: { term: keyof typeof BEGINNER_GLOSSARY | string }) {
  const t = getTermHelp(String(term));
  return <HelpTooltip term={t.term} definition={t.definition} example={t.example} />;
}
